// Ref: BE CODE-007 (case-logistics-polymorphic-schema)
import * as v from 'valibot';

import {
  ECommercialLegType,
  ELegType,
  ELocationResponseType,
  ELocationType,
  ELogisticProviderType,
  EResourceResponseType,
  EResourceType,
  ERouteLegResponseType,
  ERouteType,
  ESegmentType,
  ETransportationType,
  ETransportProviderType,
  EVehicleType,
} from '../types';

import type {
  AccommodationRouteLegResponse,
  AircraftResourceResponse,
  AirportLocationResponse,
  AirportReferenceResponse,
  CaseLogisticsLocation,
  CaseLogisticsResource,
  CaseLogisticsResponse,
  CaseLogisticsRouteLeg,
  ChainPositionResponse,
  CommercialFlightAviaLegResponse,
  CrewMemberResponse,
  CrewResourceResponse,
  CurriServiceGroundLegResponse,
  CustomAddressResponse,
  EmployeeResourceResponse,
  FboLocationAddressResponse,
  FboLocationResponse,
  FboReferenceResponse,
  GroundLocationReferenceResponse,
  HomeLocationResponse,
  HospitalLocationResponse,
  HubLocationResponse,
  LocationReferenceResponse,
  LogisticsProviderReferenceResponse,
  LogisticsRouteResponse,
  OcsConsoleResourceResponse,
  ResourceReferenceResponse,
  SelfServicedGroundLegResponse,
  SupplementalLiftAviaLegResponse,
  TmdxAffiliateGroundLegResponse,
  TmdxDriveAffiliateGroundLegResponse,
  TmdxDriveTedsGroundLegResponse,
  TmdxJetAviaLegResponse,
  TxCenterLocationResponse,
  TxCenterProviderAviaLegResponse,
  TxCenterProviderGroundLegResponse,
} from '../types';

/* ─────────── Value Object Schemas ─────────── */

const fboReferenceResponseSchema: v.GenericSchema<FboReferenceResponse> =
  v.object({
    fboLocationId: v.string('FBO location ID is missing'),
    fboAddressId: v.number('FBO address ID is missing'),
  });

const fboLocationAddressResponseSchema: v.GenericSchema<FboLocationAddressResponse> =
  v.object({
    id: v.number('FBO address ID is missing'),
    isPrimary: v.boolean('FBO address primary flag is missing'),
    type: v.string('FBO address type is missing'),
    city: v.nullish(v.string('FBO address city is missing')),
    addressLine1: v.nullish(v.string('FBO address line 1 is invalid')),
    addressLine2: v.nullish(v.string('FBO address line 2 is invalid')),
  });

const fboLocationResponseSchema: v.GenericSchema<FboLocationResponse> =
  v.object({
    id: v.string('FBO location ID is missing'),
    maoId: v.number('FBO MAO ID is missing'),
    name: v.string('FBO name is missing'),
    organizationName: v.string('FBO organization name is missing'),
    addresses: v.array(
      fboLocationAddressResponseSchema,
      'FBO addresses are invalid',
    ),
    createdAt: v.string('FBO creation date is missing'),
  });

const locationValueObjectSchema = v.object({
  country: v.nullable(v.string('Location country is invalid')),
  state: v.nullish(v.string('Location state is invalid')),
  city: v.nullish(v.string('Location city is invalid')),
  addressLine: v.nullish(v.string('Location address line is invalid')),
  postalCode: v.nullish(v.string('Location postal code is invalid')),
  timezone: v.nullish(v.string('Location timezone is invalid')),
  latitude: v.nullish(v.number('Location latitude is invalid')),
  longitude: v.nullish(v.number('Location longitude is invalid')),
});

const locationReferenceResponseSchema: v.GenericSchema<LocationReferenceResponse> =
  v.object({
    locationId: v.string('Location reference ID is missing'),
    locationType: v.enum(ELocationType, 'Location reference type is incorrect'),
  });

const airportReferenceResponseSchema: v.GenericSchema<AirportReferenceResponse> =
  v.object({
    locationId: v.string('Airport reference location ID is missing'),
    airportId: v.string('Airport ID is missing'),
    fboReference: v.nullish(fboReferenceResponseSchema),
    locationType: v.literal(
      ELocationType.Airport,
      'Airport reference location type is incorrect',
    ),
  });

const groundLocationReferenceResponseSchema: v.GenericSchema<GroundLocationReferenceResponse> =
  v.object({
    locationId: v.string('Ground location reference ID is missing'),
    airportReference: v.nullish(airportReferenceResponseSchema),
    locationReference: v.nullish(locationReferenceResponseSchema),
    locationType: v.enum(ELocationType, 'Ground location type is incorrect'),
  });

const chainPositionResponseSchema: v.GenericSchema<ChainPositionResponse> =
  v.object({
    previousLegId: v.nullish(v.string('Chain previous leg ID is invalid')),
    nextLegId: v.nullish(v.string('Chain next leg ID is invalid')),
    groupId: v.string('Chain group ID is missing'),
  });

const crewMemberResponseSchema: v.GenericSchema<CrewMemberResponse> = v.object({
  firstName: v.nullish(v.string('Crew member first name is invalid')),
  lastName: v.nullish(v.string('Crew member last name is invalid')),
  phoneNumber: v.nullish(v.string('Crew member phone number is invalid')),
});

const logisticsProviderReferenceResponseSchema: v.GenericSchema<LogisticsProviderReferenceResponse> =
  v.object({
    providerId: v.string('Provider reference ID is missing'),
    providerType: v.enum(
      ELogisticProviderType,
      'Provider reference type is incorrect',
    ),
  });

const resourceReferenceResponseSchema: v.GenericSchema<ResourceReferenceResponse> =
  v.object({
    resourceId: v.string('Resource reference ID is missing'),
    resourceType: v.enum(EResourceType, 'Resource reference type is incorrect'),
  });

/* ─────────── Polymorphic Location Schemas ─────────── */

const baseLocationFields = {
  id: v.string('Location ID is missing'),
  location: locationValueObjectSchema,
  createdAt: v.string('Location creation date is missing'),
};

const airportLocationResponseSchema: v.GenericSchema<AirportLocationResponse> =
  v.object({
    ...baseLocationFields,
    $type: v.literal(
      ELocationResponseType.Airport,
      'Airport location discriminator is incorrect',
    ),
    locationType: v.literal(
      ELocationType.Airport,
      'Airport location type is incorrect',
    ),
    maoId: v.number('Airport MAO ID is missing'),
    name: v.string('Airport name is missing'),
    icao: v.nullish(v.string('Airport ICAO is invalid')),
    iata: v.nullish(v.string('Airport IATA is invalid')),
    faa: v.nullish(v.string('Airport FAA is invalid')),
    isSabreSupported: v.boolean('Airport Sabre support flag is missing'),
    fboLocations: v.array(
      fboLocationResponseSchema,
      'Airport FBO locations are invalid',
    ),
  });

const customAddressLocationSchema: v.GenericSchema<CustomAddressResponse> =
  v.object({
    ...baseLocationFields,
    $type: v.literal(
      ELocationResponseType.CustomAddress,
      'Custom address discriminator is incorrect',
    ),
    locationType: v.literal(
      ELocationType.Custom,
      'Custom address location type is incorrect',
    ),
    hereMapsId: v.string('HERE Maps ID is missing'),
  });

const hospitalLocationResponseSchema: v.GenericSchema<HospitalLocationResponse> =
  v.object({
    ...baseLocationFields,
    $type: v.literal(
      ELocationResponseType.Hospital,
      'Hospital location discriminator is incorrect',
    ),
    locationType: v.literal(
      ELocationType.Hospital,
      'Hospital location type is incorrect',
    ),
    name: v.string('Hospital name is missing'),
    code: v.nullish(v.string('Hospital code is invalid')),
    logoPath: v.nullish(v.string('Hospital logo path is invalid')),
  });

const homeLocationResponseSchema: v.GenericSchema<HomeLocationResponse> =
  v.object({
    ...baseLocationFields,
    $type: v.literal(
      ELocationResponseType.Home,
      'Home location discriminator is incorrect',
    ),
    locationType: v.literal(
      ELocationType.HomeResidence,
      'Home location type is incorrect',
    ),
  });

const hubLocationResponseSchema: v.GenericSchema<HubLocationResponse> =
  v.object({
    ...baseLocationFields,
    $type: v.literal(
      ELocationResponseType.Hub,
      'Hub location discriminator is incorrect',
    ),
    locationType: v.literal(
      ELocationType.Hub,
      'Hub location type is incorrect',
    ),
    name: v.string('Hub name is missing'),
    code: v.string('Hub code is missing'),
  });

const txCenterLocationResponseSchema: v.GenericSchema<TxCenterLocationResponse> =
  v.object({
    ...baseLocationFields,
    $type: v.literal(
      ELocationResponseType.TxCenter,
      'TxCenter location discriminator is incorrect',
    ),
    locationType: v.literal(
      ELocationType.TxCenter,
      'TxCenter location type is incorrect',
    ),
    name: v.string('TxCenter name is missing'),
    code: v.string('TxCenter code is missing'),
    logoPath: v.nullish(v.string('TxCenter logo path is invalid')),
  });

const caseLogisticsLocationSchema: v.GenericSchema<CaseLogisticsLocation> =
  v.union([
    airportLocationResponseSchema,
    customAddressLocationSchema,
    hospitalLocationResponseSchema,
    homeLocationResponseSchema,
    hubLocationResponseSchema,
    txCenterLocationResponseSchema,
  ]);

/* ─────────── Polymorphic Resource Schemas ─────────── */

const aircraftResourceResponseSchema: v.GenericSchema<AircraftResourceResponse> =
  v.object({
    $type: v.literal(
      EResourceResponseType.Aircraft,
      'Aircraft resource discriminator is incorrect',
    ),
    resourceId: v.string('Aircraft resource ID is missing'),
    resourceType: v.literal(
      EResourceType.Aircraft,
      'Aircraft resource type is incorrect',
    ),
    serialNumber: v.nullish(v.string('Aircraft serial number is invalid')),
    tailNumber: v.string('Aircraft tail number is missing'),
    createdAt: v.string('Aircraft resource creation date is missing'),
  });

const crewResourceResponseSchema: v.GenericSchema<CrewResourceResponse> =
  v.object({
    $type: v.literal(
      EResourceResponseType.Crew,
      'Crew resource discriminator is incorrect',
    ),
    resourceId: v.string('Crew resource ID is missing'),
    resourceType: v.literal(
      EResourceType.Crew,
      'Crew resource type is incorrect',
    ),
    maoId: v.number('Crew MAO ID is missing'),
    code: v.nullish(v.string('Crew code is invalid')),
    firstName: v.nullish(v.string('Crew first name is invalid')),
    lastName: v.nullish(v.string('Crew last name is invalid')),
    phoneNumber: v.nullish(v.string('Crew phone number is invalid')),
    createdAt: v.string('Crew resource creation date is missing'),
  });

const employeeResourceResponseSchema: v.GenericSchema<EmployeeResourceResponse> =
  v.object({
    $type: v.literal(
      EResourceResponseType.Employee,
      'Employee resource discriminator is incorrect',
    ),
    resourceId: v.string('Employee resource ID is missing'),
    resourceType: v.picklist(
      [EResourceType.ClinicalSpecialist, EResourceType.Surgeon],
      'Employee resource type is incorrect',
    ),
    userId: v.nullish(v.string('Employee user ID is invalid')),
    firstName: v.string('Employee first name is missing'),
    lastName: v.string('Employee last name is missing'),
    phoneNumber: v.nullish(v.string('Employee phone number is invalid')),
    email: v.nullish(v.string('Employee email is invalid')),
    photoPath: v.nullish(v.string('Employee photo path is invalid')),
    createdAt: v.string('Employee resource creation date is missing'),
  });

const ocsConsoleResourceResponseSchema: v.GenericSchema<OcsConsoleResourceResponse> =
  v.object({
    $type: v.literal(
      EResourceResponseType.OcsConsole,
      'OCS Console resource discriminator is incorrect',
    ),
    resourceId: v.string('OCS Console resource ID is missing'),
    resourceType: v.literal(
      EResourceType.OCSConsole,
      'OCS Console resource type is incorrect',
    ),
    name: v.nullish(v.string('OCS Console name is invalid')),
    serialNumber: v.nullish(v.string('OCS Console serial number is invalid')),
    fullSerialNumber: v.nullish(
      v.string('OCS Console full serial number is invalid'),
    ),
    logoPath: v.nullish(v.string('OCS Console logo path is invalid')),
    createdAt: v.string('OCS Console resource creation date is missing'),
  });

const caseLogisticsResourceSchema: v.GenericSchema<CaseLogisticsResource> =
  v.union([
    aircraftResourceResponseSchema,
    crewResourceResponseSchema,
    employeeResourceResponseSchema,
    ocsConsoleResourceResponseSchema,
  ]);

/* ─────────── Shared Base Leg Field Groups ─────────── */

const baseRouteLegFields = {
  id: v.string('Leg ID is missing'),
  segmentType: v.enum(ESegmentType, 'Leg segment type is incorrect'),
  resources: v.array(
    resourceReferenceResponseSchema,
    'Leg resources are invalid',
  ),
  createdAt: v.string('Leg creation date is missing'),
  modifiedAt: v.string('Leg modification date is missing'),
};

const baseAviaLegFields = {
  ...baseRouteLegFields,
  chainPosition: chainPositionResponseSchema,
  legType: v.literal(ELegType.Air, 'Avia leg type field is incorrect'),
  departureLocation: airportReferenceResponseSchema,
  arrivalLocation: airportReferenceResponseSchema,
  departureTime: v.string('Avia leg departure time is missing'),
  arrivalTime: v.string('Avia leg arrival time is missing'),
  departureLocalTimezone: v.string('Avia leg departure timezone is missing'),
  arrivalLocalTimezone: v.string('Avia leg arrival timezone is missing'),
  comments: v.nullish(v.string('Avia leg comment is invalid')),
  hasScs: v.nullish(v.boolean('Avia leg SCS flag is invalid')),
};

const baseGroundLegFields = {
  ...baseRouteLegFields,
  chainPosition: chainPositionResponseSchema,
  legType: v.literal(ELegType.Ground, 'Ground leg type field is incorrect'),
  departureLocation: groundLocationReferenceResponseSchema,
  arrivalLocation: groundLocationReferenceResponseSchema,
  departureTime: v.string('Ground leg departure time is missing'),
  arrivalTime: v.string('Ground leg arrival time is missing'),
  departureLocalTimezone: v.string('Ground leg departure timezone is missing'),
  arrivalLocalTimezone: v.string('Ground leg arrival timezone is missing'),
  distanceInMeters: v.number('Ground leg distance in meters is missing'),
  comments: v.nullish(v.string('Ground leg comment is invalid')),
  hasScs: v.nullish(v.boolean('Ground leg SCS flag is invalid')),
};

// Driver-detail fields shared by TmdxAffiliate and TxCenterProvider ground legs
const driverGroundLegFields = {
  driver: v.nullish(v.string('Ground leg driver name is invalid')),
  email: v.nullish(v.string('Ground leg driver email is invalid')),
  phoneNumber: v.nullish(v.string('Ground leg driver phone number is invalid')),
  plateNumber: v.nullish(v.string('Ground leg plate number is invalid')),
  vehicleType: v.nullish(
    v.enum(EVehicleType, 'Ground leg vehicle type is incorrect'),
  ),
  reservationNumber: v.nullish(
    v.string('Ground leg reservation number is invalid'),
  ),
  pickup: v.nullish(v.string('Ground leg pickup is invalid')),
  dropoff: v.nullish(v.string('Ground leg dropoff is invalid')),
  wait: v.nullish(v.string('Ground leg wait is invalid')),
  stay: v.nullish(v.string('Ground leg stay is invalid')),
  isFeasibleForDrive: v.boolean('Ground leg drive feasibility flag is missing'),
};

/* ─────────── Avia Leg Schemas ─────────── */

const tmdxJetAviaLegResponseSchema: v.GenericSchema<TmdxJetAviaLegResponse> =
  v.object({
    ...baseAviaLegFields,
    $type: v.literal(
      ERouteLegResponseType.TmdxJetAviaLeg,
      'TMDX jet avia leg type is incorrect',
    ),
    transportProviderType: v.literal(
      ETransportProviderType.Tmdx,
      'TMDX jet avia provider type is incorrect',
    ),
  });

const supplementalLiftAviaLegResponseSchema: v.GenericSchema<SupplementalLiftAviaLegResponse> =
  v.object({
    ...baseAviaLegFields,
    $type: v.literal(
      ERouteLegResponseType.SupplementalLiftAviaLeg,
      'Supplemental lift avia leg type is incorrect',
    ),
    transportProviderType: v.literal(
      ETransportProviderType.SupplementalLiftProvider,
      'Supplemental lift provider type is incorrect',
    ),
    provider: logisticsProviderReferenceResponseSchema,
    firstCrewMember: crewMemberResponseSchema,
    secondCrewMember: crewMemberResponseSchema,
    tailNumber: v.nullish(v.string('Supplemental lift tail number is invalid')),
    priceInCents: v.number('Supplemental lift price is missing'),
  });

const txCenterProviderAviaLegResponseSchema: v.GenericSchema<TxCenterProviderAviaLegResponse> =
  v.object({
    ...baseAviaLegFields,
    $type: v.literal(
      ERouteLegResponseType.TxCenterProviderAviaLeg,
      'TxCenter avia leg type is incorrect',
    ),
    transportProviderType: v.literal(
      ETransportProviderType.TxCenter,
      'TxCenter avia provider type is incorrect',
    ),
    provider: logisticsProviderReferenceResponseSchema,
    firstCrewMember: crewMemberResponseSchema,
    secondCrewMember: crewMemberResponseSchema,
    tailNumber: v.nullish(v.string('TxCenter avia leg tail number is invalid')),
  });

const commercialFlightAviaLegResponseSchema: v.GenericSchema<CommercialFlightAviaLegResponse> =
  v.object({
    ...baseAviaLegFields,
    $type: v.literal(
      ERouteLegResponseType.CommercialFlightAviaLeg,
      'Commercial flight leg type is incorrect',
    ),
    transportProviderType: v.literal(
      ETransportProviderType.Commercial,
      'Commercial flight provider type is incorrect',
    ),
    sabreGroupId: v.nullish(
      v.string('Commercial flight Sabre group ID is invalid'),
    ),
    flightNumber: v.string('Commercial flight number is missing'),
    bookingNumber: v.nullish(
      v.string('Commercial flight booking number is invalid'),
    ),
    priceInCents: v.nullish(v.number('Commercial flight price is invalid')),
    flightOperator: v.nullish(
      v.string('Commercial flight operator is invalid'),
    ),
    seatType: v.nullish(v.string('Commercial flight seat type is invalid')),
    numberOfSeats: v.nullish(
      v.number('Commercial flight seat count is invalid'),
    ),
    commercialLegType: v.enum(
      ECommercialLegType,
      'Commercial leg type is incorrect',
    ),
  });

/* ─────────── Ground Leg Schemas ─────────── */

const tmdxAffiliateGroundLegResponseSchema: v.GenericSchema<TmdxAffiliateGroundLegResponse> =
  v.object({
    ...baseGroundLegFields,
    ...driverGroundLegFields,
    $type: v.literal(
      ERouteLegResponseType.TmdxAffiliateGroundLeg,
      'TMDX affiliate ground leg type is incorrect',
    ),
    transportProviderType: v.literal(
      ETransportProviderType.TMDXAffiliate,
      'TMDX affiliate provider type is incorrect',
    ),
  });

const txCenterProviderGroundLegResponseSchema: v.GenericSchema<TxCenterProviderGroundLegResponse> =
  v.object({
    ...baseGroundLegFields,
    ...driverGroundLegFields,
    $type: v.literal(
      ERouteLegResponseType.TxCenterProviderGroundLeg,
      'TxCenter ground leg type is incorrect',
    ),
    transportProviderType: v.literal(
      ETransportProviderType.TxCenter,
      'TxCenter ground provider type is incorrect',
    ),
    provider: logisticsProviderReferenceResponseSchema,
  });

const curriServiceGroundLegResponseSchema: v.GenericSchema<CurriServiceGroundLegResponse> =
  v.object({
    ...baseGroundLegFields,
    $type: v.literal(
      ERouteLegResponseType.CurriServiceGroundLeg,
      'Curri service ground leg type is incorrect',
    ),
    transportProviderType: v.literal(
      ETransportProviderType.Curri,
      'Curri service provider type is incorrect',
    ),
    trackingNumber: v.nullish(
      v.string('Curri service tracking number is invalid'),
    ),
  });

const tmdxDriveAffiliateGroundLegResponseSchema: v.GenericSchema<TmdxDriveAffiliateGroundLegResponse> =
  v.object({
    ...baseGroundLegFields,
    $type: v.literal(
      ERouteLegResponseType.TmdxDriveAffiliateGroundLeg,
      'TMDX drive affiliate ground leg type is incorrect',
    ),
    transportProviderType: v.literal(
      ETransportProviderType.TMDXAffiliate,
      'TMDX drive affiliate provider type is incorrect',
    ),
    isFeasibleForDrive: v.boolean(
      'TMDX drive affiliate feasibility flag is missing',
    ),
  });

const tmdxDriveTedsGroundLegResponseSchema: v.GenericSchema<TmdxDriveTedsGroundLegResponse> =
  v.object({
    ...baseGroundLegFields,
    $type: v.literal(
      ERouteLegResponseType.TmdxDriveTedsGroundLeg,
      'TMDX drive Teds ground leg type is incorrect',
    ),
    transportProviderType: v.literal(
      ETransportProviderType.Teds,
      'TMDX drive Teds provider type is incorrect',
    ),
    isFeasibleForDrive: v.boolean(
      'TMDX drive Teds feasibility flag is missing',
    ),
  });

// SpecRef: CODE-007
const selfServicedGroundLegResponseSchema: v.GenericSchema<SelfServicedGroundLegResponse> =
  v.object({
    ...baseGroundLegFields,
    $type: v.literal(
      ERouteLegResponseType.SelfServicedGroundLeg,
      'Self-serviced ground leg type is incorrect',
    ),
    transportProviderType: v.literal(
      ETransportProviderType.SelfServiced,
      'Self-serviced provider type is incorrect',
    ),
    isFeasibleForDrive: v.boolean('Self-serviced feasibility flag is missing'),
  });

/* ─────────── Accommodation Leg Schema ─────────── */

const accommodationRouteLegResponseSchema: v.GenericSchema<AccommodationRouteLegResponse> =
  v.object({
    ...baseRouteLegFields,
    $type: v.literal(
      ERouteLegResponseType.AccommodationLeg,
      'Accommodation leg type is incorrect',
    ),
    legType: v.literal(
      ELegType.Accommodation,
      'Accommodation leg type field is incorrect',
    ),
    location: v.nullish(locationReferenceResponseSchema),
    checkInAt: v.string('Accommodation check-in time is missing'),
    checkOutAt: v.string('Accommodation check-out time is missing'),
    rooms: v.nullish(v.number('Accommodation rooms count is invalid')),
    hotelName: v.nullish(v.string('Accommodation hotel name is invalid')),
    bookingNumber: v.nullish(
      v.string('Accommodation booking number is invalid'),
    ),
    comments: v.nullish(v.string('Accommodation leg comment is invalid')),
    hasScs: v.nullish(v.boolean('Accommodation leg SCS flag is invalid')),
  });

/* ─────────── Route Leg Union Schema ─────────── */

const caseLogisticsRouteLegSchema: v.GenericSchema<CaseLogisticsRouteLeg> =
  v.union([
    tmdxJetAviaLegResponseSchema,
    supplementalLiftAviaLegResponseSchema,
    txCenterProviderAviaLegResponseSchema,
    commercialFlightAviaLegResponseSchema,
    tmdxAffiliateGroundLegResponseSchema,
    txCenterProviderGroundLegResponseSchema,
    curriServiceGroundLegResponseSchema,
    tmdxDriveAffiliateGroundLegResponseSchema,
    tmdxDriveTedsGroundLegResponseSchema,
    selfServicedGroundLegResponseSchema,
    accommodationRouteLegResponseSchema,
  ]);

/* ─────────── Route Schema ─────────── */

const logisticsRouteResponseSchema: v.GenericSchema<LogisticsRouteResponse> =
  v.object({
    id: v.string('Route ID is missing'),
    routeType: v.enum(ERouteType, 'Route type is incorrect'),
    transportationType: v.enum(
      ETransportationType,
      'Route transportation type is incorrect',
    ),
    departureHub: v.nullish(locationReferenceResponseSchema),
    arrivalHub: v.nullish(locationReferenceResponseSchema),
    legs: v.array(caseLogisticsRouteLegSchema, 'Route legs are invalid'),
    createdAt: v.string('Route creation date is missing'),
    modifiedAt: v.string('Route modification date is missing'),
  });

/* ─────────── CaseLogisticsResponse Schema ─────────── */

export const caseLogisticsResponseSchema: v.GenericSchema<CaseLogisticsResponse> =
  v.object({
    id: v.string('Case logistics ID is missing'),
    caseId: v.string('Case ID is missing'),
    // Non-nullable since the NOP contract sync of 2026-07-16 (routeservice
    // dropped `null` from the field). A lagging NOP may still send null —
    // the array-level message MUST stay @WARNING: an unprefixed (ERROR)
    // issue here would make validateAndTransformResponse drop the WHOLE
    // active case from the map, not just this unused field.
    logisticsProviders: v.array(
      v.object({
        id: v.string('Logistics provider ID is missing'),
        name: v.nullish(v.string('Logistics provider name is invalid')),
        providerType: v.enum(
          ELogisticProviderType,
          'Logistics provider type is incorrect',
        ),
        createdAt: v.string('Logistics provider creation date is missing'),
      }),
      '@WARNING: Logistics providers missing or null (non-nullable per contract)',
    ),
    resources: v.array(
      caseLogisticsResourceSchema,
      'Case logistics resources are invalid',
    ),
    locations: v.array(
      caseLogisticsLocationSchema,
      'Case logistics locations are invalid',
    ),
    routes: v.array(
      logisticsRouteResponseSchema,
      'Case logistics routes are invalid',
    ),
    needAviationApproval: v.nullish(
      v.boolean('Aviation approval flag is invalid'),
    ),
    version: v.number('Logistics version is missing'),
    createdAt: v.string('Logistics creation date is missing'),
    modifiedAt: v.string('Logistics modification date is missing'),
    archivedAt: v.nullish(v.string('Logistics archive date is invalid')),
    archivedReason: v.nullish(v.string('Logistics archive reason is invalid')),
    isTmdxDriveCompatible: v.nullish(
      v.boolean('TMDX drive compatible flag is missing'),
    ),
  });
